import { useContext, useMemo } from 'react';
import { ModalContext } from './ModalProvider';
import type { ModalContextValue, ModalConfig } from './types';
import { MODAL_CONFIGS, getModalDefaultProps } from './registry';
import type { ModalType } from './registry';

/**
 * Hook to consume the ModalContext
 * Provides convenient access to modal management functions
 *
 * @returns ModalContextValue - Contains all modal management functions
 *
 * @example
 * ```tsx
 * const { openModal, closeModal, isModalOpen } = useModal();
 *
 * // Open a modal
 * const modalId = openModal({
 *   type: 'CostsAndFees',
 *   priority: 2,
 *   onClose: () => console.log('Modal closed')
 * });
 *
 * // Check if modal is open
 * if (isModalOpen('CostsAndFees')) {
 *   console.log('CostsAndFees modal is open');
 * }
 *
 * // Close the modal
 * closeModal(modalId);
 * ```
 */
export const useModal = (): ModalContextValue => {
  const context = useContext(ModalContext);

  if (!context) {
    // Return a safe default during SSR
    return {
      activeModals: [],
      openModal: () => '',
      closeModal: () => {},
      closeAllModals: () => {},
      closeModalByType: () => {},
      isModalOpen: () => false,
      getModalById: () => undefined,
    };
  }

  return context;
};

// Type helper for modal opener function - simplified
type ModalOpenerFunction = (props?: Omit<ModalConfig, 'id' | 'type'>) => string;

// Auto-generate modal opener functions from config
type ModalOpeners = {
  [K in ModalType as `open${Capitalize<K>}Modal`]: ModalOpenerFunction;
};

/**
 * Type-safe modal opener hook with auto-generated methods
 * Fixed architecture: Uses memoization and error handling to prevent rendering issues
 * @returns Function to open modals with type safety and default props applied
 */
export const useModalOpener = (): ModalOpeners => {
  const { openModal } = useModal();

  // Memoize opener methods to prevent recreation on every render
  // This fixes the architectural flaw that was causing homepage rendering issues
  const openers = useMemo(() => {
    // SSR safety: If openModal is the fallback function (returns empty string),
    // we know we're in SSR mode and should return safe fallbacks
    if (openModal.toString().includes("return ''")) {
      console.warn('useModalOpener: SSR mode detected, returning safe fallbacks');
      return {} as ModalOpeners;
    }
    const modalOpeners = {} as ModalOpeners;

    try {
      // Process modal configurations safely with error boundaries
      (Object.keys(MODAL_CONFIGS) as ModalType[]).forEach((modalType) => {
        // Skip if modal config is invalid
        if (!modalType || !MODAL_CONFIGS[modalType]) {
          console.warn(`Skipping invalid modal type: ${modalType}`);
          return;
        }

        const methodName = `open${modalType.charAt(0).toUpperCase() + modalType.slice(1)}Modal` as keyof ModalOpeners;

        modalOpeners[methodName] = ((props: Omit<ModalConfig, 'id' | 'type'> = {}) => {
          try {
            const defaultProps = getModalDefaultProps(modalType);

            // Merge props correctly with defensive checks
            const mergedConfig: Omit<ModalConfig, 'id'> = {
              ...props,
              type: modalType,
              props: {
                ...defaultProps, // Default style props (size, padding, etc.)
                ...(props.props || {}), // Component-specific props from caller (with fallback)
              },
            };

            return openModal(mergedConfig);
          } catch (error) {
            console.error(`Error opening ${modalType} modal:`, error);
            return '';
          }
        }) as ModalOpenerFunction;
      });
    } catch (error) {
      console.error('Error processing modal configurations:', error);
    }

    return modalOpeners;
  }, [openModal]); // Only recreate if openModal changes

  return openers;
};

/**
 * Hook to check if a specific modal type is open
 * @param type - The modal type to check
 * @returns Boolean indicating if the modal is open
 */
export const useIsModalOpen = (type: ModalType): boolean => {
  const { isModalOpen } = useModal();
  return isModalOpen(type);
};

/**
 * Hook to get active modals count
 * @returns Number of active modals
 */
export const useActiveModalsCount = (): number => {
  const { activeModals } = useModal();
  return activeModals.length;
};

export default useModal;
